import { ArrowUpRight, Clock3, CreditCard, MapPin, Printer, QrCode, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';

const steps = [
  {
    icon: QrCode,
    title: 'Scan',
    description: 'Scan the in-store StatioPrint QR to open the print flow on your phone.',
  },
  {
    icon: Upload,
    title: 'Upload',
    description: 'Choose documents, set print preferences, and confirm the job in seconds.',
  },
  {
    icon: CreditCard,
    title: 'Pay',
    description: 'Continue with online payment or complete the order at the counter.',
  },
  {
    icon: Printer,
    title: 'Collect',
    description:
      'Pick up your prints at the shop while StatioMart stays the parent brand throughout.',
  },
];

const storeDetails = [
  'Shop No.36, Nathu Singh Market, Masoodpur, Vasant Kunj, New Delhi - 110070',
  '15-20 minute local delivery for StatioMart orders',
  'Serving nearby South Delhi pincodes around Vasant Kunj',
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-800">
      <section className="px-4 pt-6 pb-18 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <img
                src="/statiomart.png"
                alt="StatioMart"
                className="h-12 w-12 rounded-2xl object-cover"
              />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                  Powered by StatioMart
                </p>
                <h1 className="text-2xl font-semibold text-slate-900">StatioPrint</h1>
              </div>
            </div>

            <a
              href="https://statiomart.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 self-start rounded-full border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:border-slate-500"
            >
              Visit statiomart.com
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-4xl pt-16 pb-10 text-center sm:pt-24">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/80 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
              <Clock3 size={14} />
              Omnichannel stationery, now with seamless print pickup
            </div>

            <h2 className="mx-auto mt-6 max-w-3xl text-5xl font-semibold tracking-[-0.05em] text-slate-950 sm:text-6xl">
              Print simply. Pick up fast. Stay inside the StatioMart ecosystem.
            </h2>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
              StatioPrint is the print experience from StatioMart. Customers scan the QR at the
              shop, upload documents, pay quickly, and collect at the counter while StatioMart
              remains the parent brand across store, support, and stationery orders.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <div className="inline-flex min-w-[240px] items-center justify-center rounded-full bg-indigo-600 px-6 py-3 text-sm font-medium text-white">
                Scan the shop QR to start
              </div>
              <a
                href="https://statiomart.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-full border border-slate-300 bg-white/85 px-6 py-3 text-sm font-medium text-slate-800 transition-colors hover:border-slate-500"
              >
                Shop on StatioMart
                <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl border-t border-slate-200 pt-8">
          <div className="grid gap-4 text-left md:grid-cols-3">
            <div className="rounded-[1.5rem] bg-white/70 p-5">
              <p className="text-3xl font-semibold tracking-[-0.04em] text-slate-950">15-20 min</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Fast local delivery for StatioMart orders within the service area.
              </p>
            </div>
            <div className="rounded-[1.5rem] bg-white/70 p-5">
              <p className="text-3xl font-semibold tracking-[-0.04em] text-slate-950">
                Shop No. 36
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Nathu Singh Market, Masoodpur, Vasant Kunj, New Delhi.
              </p>
            </div>
            <div className="rounded-[1.5rem] bg-white/70 p-5">
              <p className="text-3xl font-semibold tracking-[-0.04em] text-slate-950">7 pincodes</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                110070, 110067, 110074, 110038, 110057, 110016, 110030.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-6 sm:px-6 lg:px-10">
        <div className="mx-auto mt-16 max-w-5xl">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
              How It Works
            </p>
            <h3 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
              A quieter, faster print flow.
            </h3>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="rounded-[1.75rem] border border-slate-200 bg-white/70 p-6 shadow-[0_10px_35px_rgba(15,23,42,0.04)] backdrop-blur-sm"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700">
                    <Icon size={18} />
                  </div>
                  <h4 className="mt-6 text-xl font-semibold text-slate-950">{step.title}</h4>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 pb-6 sm:px-6 lg:px-10">
        <div className="mx-auto mt-16 grid max-w-5xl gap-8 rounded-[2rem] border border-slate-200 bg-white/70 px-6 py-8 shadow-[0_12px_40px_rgba(15,23,42,0.05)] backdrop-blur-sm lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
              StatioMart Context
            </p>
            <h3 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
              StatioPrint belongs to the StatioMart brand, not outside it.
            </h3>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600">
              This homepage positions print as one focused product inside the larger StatioMart
              experience: local retail, fast online ordering, and one recognizable identity across
              stationery and document services.
            </p>
          </div>

          <div className="space-y-4">
            {storeDetails.map((detail) => (
              <div
                key={detail}
                className="flex items-start gap-3 rounded-[1.25rem] bg-white/80 px-4 py-4"
              >
                <MapPin size={16} className="mt-1 shrink-0 text-slate-500" />
                <p className="text-sm leading-6 text-slate-600">{detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-4 pb-20 sm:px-6 lg:px-10">
        <div className="mx-auto mt-12 max-w-5xl text-center">
          <p className="text-sm text-slate-500">
            Need the operator side of StatioPrint?{' '}
            <Link to="/seller" className="font-medium text-slate-800 hover:underline">
              For shop owners
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
